import { Moon, X } from "lucide-react";
import { toast } from "sonner";
import { useCalmMode } from "./CalmModeContext";
import { calmModeSections } from "./data/homeOsMockData";

const sections = calmModeSections;

export function CalmModeModal({ open, onClose }) {
  const { setIsCalm } = useCalmMode();

  if (!open) return null;

  const activate = () => {
    setIsCalm(true);
    onClose();
    toast("Calm Mode is on. ARA has simplified today to the essentials.", { duration: 5000 });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="calm-mode-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-2xl font-home animate-fadeUp" data-testid="calm-mode-card">
        {/* Header */}
        <div className="sticky top-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-50 dark:from-teal-900/40 dark:to-cyan-900/30 flex items-center justify-center">
                <Moon className="w-5.5 h-5.5 text-teal-600 dark:text-teal-400" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Calm Mode</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">ARA will reduce the household plan to essentials only.</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-testid="calm-mode-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Sections */}
        <div className="px-6 py-5 space-y-3">
          {sections.map(section => {
            const SIcon = section.icon;
            return (
              <div key={section.title} className={`rounded-[20px] border p-4 ${section.color}`} data-testid={`calm-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <SIcon className={`w-4 h-4 ${section.iconColor}`} strokeWidth={1.5} />
                  <h3 className="text-sm font-extrabold">{section.title}</h3>
                </div>
                <ul className="space-y-1.5">
                  {section.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex items-center gap-3">
          <button
            onClick={activate}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm py-3 rounded-2xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.97]"
            data-testid="activate-calm-mode-btn"
          >
            Activate Calm Mode
          </button>
          <button
            onClick={onClose}
            className="px-5 py-3 rounded-2xl text-sm font-bold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="cancel-calm-mode-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* Status pill for dashboard header */
export function CalmModeStatusPill() {
  const { isCalm, setIsCalm } = useCalmMode();
  if (!isCalm) return null;

  return (
    <button
      onClick={() => { setIsCalm(false); toast("Calm Mode turned off. Full household plan restored."); }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-bold hover:bg-teal-200 dark:hover:bg-teal-900/50 transition-colors animate-fadeUp"
      data-testid="calm-mode-active-pill"
    >
      <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />
      Calm Mode Active
    </button>
  );
}
